import { Injectable } from '@angular/core';
import { showInvoiceAndPayments } from '@app/user/store';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { PaymentDetailsModalComponent } from '@payments/components/payment-details-modal/payment-details-modal.component';
import {
  getTransactionNumbers,
  getUserTypesThatCanSubmit,
} from '@payments/selectors/payments.selector';
import { CancelPendingPaymentsAnalyticsService } from '@payments/tracking/analytics/cancel-pending-payments/cancel-pending-payments-analytics.service';
import { FEATURES } from '@shared/constants/splitio-features';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { PaymentStoreService, paymentSelectors } from '@usf/ngrx-payment';
import {
  GetPaymentDetailsResponse,
  PaymentActivity,
  PaymentTransactionsViewModel,
  Status,
} from '@usf/payment-types';
import {
  Observable,
  combineLatest,
  filter,
  map,
  skipWhile,
  take,
  tap,
} from 'rxjs';
import { InvoiceIsrService } from '../invoice-isr.service';
import { PaymentsService } from '../payments.service';

@Injectable({
  providedIn: 'root',
})
export class CancelPendingPaymentsService {
  constructor(
    private translateService: TranslateService,
    private invoiceIsrService: InvoiceIsrService,
    readonly panAppState: PanAppState,
    private modalService: ModalService,
    private modalController: ModalController,
    private platformService: PlatformService,
    private store: Store,
    private paymentStore: PaymentStoreService,
    private loadingSpinner: LoadingSpinnerService,
    private paymentsService: PaymentsService,
    private cancelPendingPaymentsAnalyticsService: CancelPendingPaymentsAnalyticsService,
  ) {}

  canCancelPayment$(): Observable<boolean> {
    return combineLatest([
      this.panAppState.feature$([
        FEATURES.split_user_payments_cancel_payment.name,
      ]),
      this.store.select(getUserTypesThatCanSubmit),
      this.store.select(showInvoiceAndPayments),
      this.panAppState.user$,
    ]).pipe(
      map(
        ([
          splitIOFlag,
          userTypeCanSubmit,
          CCAPaymentsEnabled,
          userState,
        ]): boolean =>
          splitIOFlag &&
          userTypeCanSubmit &&
          CCAPaymentsEnabled &&
          !!userState?.userId,
      ),
    );
  }

  createCancelOption(
    transaction: PaymentTransactionsViewModel,
    callback?: () => void,
  ) {
    return this.canCancelPayment$().pipe(
      map(canCancel => {
        return canCancel && transaction.status === Status.PENDING
          ? this.cancelOption(transaction.paymentActivityID, callback)
          : null;
      }),
      take(1),
    );
  }

  cancelOption(
    paymentActivityID: string,
    callback?: () => void,
    isCancelPayment = true,
  ) {
    return {
      text: this.translateService.instant(
        'i18n.payments.cancelPayment.cancelPayment',
      ),
      handler: () => {
        this.fetchPaymentDetails(paymentActivityID, isCancelPayment, callback);
        this.invoiceIsrService.dismissPopover();
      },
    };
  }

  fetchPaymentDetails(
    paymentId: string,
    isCancelPayment: boolean,
    callback?: () => void,
    canCancel: boolean = true,
  ) {
    this.store
      .select(paymentSelectors.selectPayment)
      .pipe(
        this.loadingSpinnerPipe,
        skipWhile(payments => !payments.error && !payments.paymentDetails),
        take(1),
      )
      .subscribe(payments => {
        if (!!payments.error) {
          this.handleCancelError(
            'i18n.payments.cancelPayment.getPaymentDetailsGenericError',
          );
          return;
        }
        if (!!payments.paymentDetails) {
          this.openCancelOption(
            payments.paymentDetails,
            isCancelPayment,
            canCancel,
          ).then(({ data: shouldCancel }) => {
            this.paymentStore.clearPaymentDetails();
            shouldCancel &&
              this.handleCancelPayment(
                paymentId,
                payments.paymentDetails,
                callback,
              );
          });
        }
      });
    this.paymentStore.getPaymentDetails(paymentId);
  }

  handleCancelError(message: string) {
    this.paymentsService.showToastMessage(
      this.translateService.instant(message),
      true,
    );
    this.paymentStore.clearError();
  }

  handleCancelPayment(
    paymentId: string,
    paymentDetails: PaymentActivity,
    callback?: () => void,
  ) {
    this.store
      .select(paymentSelectors.selectPayment)
      .pipe(
        this.loadingSpinnerPipe,
        skipWhile(payments => !payments.error && !payments.cancellingFlag),
        take(1),
      )
      .subscribe(payments => {
        this.paymentStore.clearCancellingFlag();
        if (!!payments.error) {
          this.handleCancelError(
            'i18n.payments.cancelPayment.cancelPaymentGenericError',
          );
        } else {
          this.cancelPendingPaymentsAnalyticsService.trackCancelPayment(
            paymentDetails.customerCount,
            paymentDetails.itemCount,
            paymentDetails.totalPaymentAmount,
          );
          callback && callback();
        }
      });
    this.paymentStore.cancelPayment(paymentId);
  }

  async openCancelOption(
    paymentDetails: GetPaymentDetailsResponse,
    isCancelPayment: boolean,
    canCancel: boolean = true,
  ) {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      PaymentDetailsModalComponent,
      {
        isCancelPayment,
        paymentDetails: {
          ...paymentDetails,
          paymentDetails: paymentDetails.paymentDetails.map(paymentDetail => ({
            ...paymentDetail,
            invoices:
              paymentDetail.invoices?.map(invoice => {
                const { transactionNumber, secondaryTransactionNumber } =
                  getTransactionNumbers(invoice);
                return {
                  ...invoice,
                  originalTransaction: transactionNumber,
                  secondaryTransaction: secondaryTransactionNumber,
                };
              }) || [],
          })),
        },
        canCancel: paymentDetails.status === Status.PENDING && canCancel,
      },
      'modal-desktop-size-lg',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
    return await modal.onDidDismiss<boolean>();
  }

  private readonly loadingSpinnerPipe = (
    src: Observable<{ loading: boolean }>,
  ): Observable<any> => {
    return src.pipe(
      tap(
        payments =>
          payments.loading && this.loadingSpinner.createSpinnerModal(),
      ),
      filter(payments => !payments.loading),
      tap(async () => await this.loadingSpinner.dismissSpinnerModal()),
    );
  };
}
